import * as Sentry from '@sentry/react'
import { ConnectError, Code } from '@connectrpc/connect'
import { toast } from 'sonner'
import { ErrorDetailSchema } from '@/gen/cortado/v1/errdetails/errdetails_pb'
import { BILLING_SUSPENDED_PATH, ONBOARDING_PATH } from './billing-gate'

type ErrorContext = {
  feature?: string
  kind?: 'query' | 'mutation' | 'render'
  [k: string]: unknown
}

type ErrorCopy = {
  title: string
  description: string
  action?: { label: string; href: string }
  // expected: true marks a user-correctable, handled error the user can resolve
  // themselves. These get a toast but are NOT reported to Sentry — they are not
  // bugs. Genuine failures (e.g. db_create_failed, supabase_create_failed) omit
  // this and are still captured.
  expected?: boolean
}

const reportedErrors = new WeakSet<object>()

export function readReason(err: unknown): string | undefined {
  if (!(err instanceof ConnectError)) return undefined
  // Details parsed off the wire are Any-wrapped protobuf bytes, not plain
  // objects — findDetails() is the only way to decode them.
  for (const detail of err.findDetails(ErrorDetailSchema)) {
    if (detail.reason) return detail.reason
  }
  return undefined
}

const REASON_MAP: Record<string, ErrorCopy> = {
  stripe_not_connected: {
    title: 'Connect Stripe first',
    description: 'You need to connect Stripe before creating paid quotes.',
    action: { label: 'Connect Stripe', href: '/payments?section=stripe' },
    expected: true,
  },
  onboarding_required: {
    title: 'Finish onboarding',
    description: 'Complete your organization setup before entering the application.',
    action: { label: 'Resume onboarding', href: ONBOARDING_PATH },
    expected: true,
  },
  subscription_required: {
    title: 'Subscription required',
    description: 'Subscribe to Cortado Pro to continue using this feature.',
    action: { label: 'Subscribe', href: '/company?section=subscription' },
    expected: true,
  },
  subscription_expired: {
    title: 'Subscription expired',
    description: 'Resubscribe to restore access to your account.',
    action: { label: 'Resubscribe', href: '/company?section=subscription' },
    expected: true,
  },
  // The transport interceptor shows an inline toast (no navigation) on this
  // reason - a read-only org can still view records and update billing.
  billing_read_only: {
    title: 'Account is in read-only mode',
    description:
      'View existing records and update billing, but new records and sends are blocked. Update billing to restore full access.',
    action: { label: 'Update billing', href: '/company?section=subscription' },
    expected: true,
  },
  // The transport interceptor (connect-transport.ts) redirects to the F8
  // full-screen suspended route on this reason. No checkout is offered there
  // (DEC-19) - a suspended account must not be able to pay its way back in.
  billing_suspended: {
    title: 'Account suspended',
    description: "Contact support to resolve this account's suspension.",
    action: { label: 'View details', href: BILLING_SUSPENDED_PATH },
    expected: true,
  },
  gmail_token_expired: {
    title: 'Reconnect Gmail',
    description: 'Your Gmail connection expired. Reconnect to keep sending quotes.',
    action: { label: 'Reconnect Gmail', href: '/email?section=senders' },
    expected: true,
  },
  gmail_insufficient_permissions: {
    title: 'Calendar access denied',
    description: "The connected Google account can't write to the selected calendar. Reconnect Google or choose a different calendar.",
    action: { label: 'Reconnect Google', href: '/email?section=senders' },
    expected: true,
  },
  supabase_create_failed: {
    title: "Couldn't create user",
    description: 'We had trouble creating this user. Please try again.',
  },
  db_create_failed: {
    title: "Couldn't save changes",
    description: 'We had trouble saving. Please try again.',
  },
  // Keep this key in sync with reasonQuotePaymentScheduleMismatch in
  // backend/internal/api/handlers/error_mapping.go.
  quote_payment_schedule_mismatch: {
    title: 'Payment schedule no longer matches the total',
    description:
      'Update the deposit or remaining amounts so they add up to the new quote total, then save again.',
    expected: true,
  },
  invoice_payment_schedule_mismatch: {
    title: 'Payment schedule no longer matches the total',
    description:
      'Open Payments and choose how to apply the invoice total change before saving.',
    expected: true,
  },
  // Keep this key in sync with reasonQuoteStatusTransitionNotAllowed in
  // backend/internal/api/handlers/error_mapping.go.
  quote_status_transition_not_allowed: {
    title: "This status change isn't allowed",
    description:
      "This quote's status can't be changed that way — accepted quotes are managed automatically.",
    expected: true,
  },
  // Keep this key in sync with reasonQuoteAcceptedFinancialsLocked in
  // backend/internal/api/handlers/error_mapping.go.
  quote_accepted_financials_locked: {
    title: 'This quote was accepted',
    description:
      "Its pricing and payment schedule can't change here — adjust payment terms on the invoice created from it.",
    expected: true,
  },
}

const FALLBACK_COPY: ErrorCopy = {
  title: 'Something went wrong',
  description: 'Please try again. If the problem persists, contact support.',
}

// Every RBAC denial (missing permission, stale permission cache, a user
// probing a URL they can't reach) comes back from the backend as a bare
// Code.PermissionDenied with no ErrorDetail reason attached — see the RBAC
// interceptor. The backend already excludes this code from Sentry capture
// (skipCaptureCodes); this mirrors that posture on the frontend.
const PERMISSION_DENIED_COPY: ErrorCopy = {
  title: "You don't have permission",
  description: "You don't have permission to do this. Contact an admin if you think this is a mistake.",
  expected: true,
}

// Exported so other UI (e.g. a "you don't have access" empty state) can
// branch on this without re-deriving the Connect code check.
export function isPermissionDeniedError(err: unknown): boolean {
  return err instanceof ConnectError && err.code === Code.PermissionDenied
}

export function reportError(err: unknown, ctx?: ErrorContext): void {
  // Gate here, not only in callers: ~80 per-feature handlers call this
  // directly, and any one of them would otherwise become the surviving Sentry
  // event for an expected failure.
  if (isExpectedError(err)) return
  if (err && typeof err === 'object') {
    if (reportedErrors.has(err)) return
    reportedErrors.add(err)
  }
  const reason = readReason(err)
  Sentry.captureException(err, {
    tags: {
      ...(ctx?.feature ? { feature: ctx.feature } : {}),
      ...(ctx?.kind ? { kind: ctx.kind } : {}),
      ...(reason ? { reason } : {}),
      ...(typeof ctx?.apiService === 'string'
        ? { api_service: ctx.apiService }
        : {}),
      ...(typeof ctx?.apiMethod === 'string'
        ? { api_method: ctx.apiMethod }
        : {}),
      ...(typeof ctx?.backendRequestId === 'string'
        ? { backend_request_id: ctx.backendRequestId }
        : {}),
      ...(typeof ctx?.backendTraceId === 'string'
        ? { backend_trace_id: ctx.backendTraceId }
        : {}),
    },
    extra: ctx,
  })
}

export function getErrorMessage(err: unknown): ErrorCopy {
  const reason = readReason(err)
  if (reason && reason in REASON_MAP) return REASON_MAP[reason]
  if (isPermissionDeniedError(err)) return PERMISSION_DENIED_COPY
  return FALLBACK_COPY
}

// Single source of truth for "is this error an expected, user-correctable
// outcome rather than a bug?". Every Sentry capture path (transport
// interceptor, global query/mutation cache handlers, mutationErrorHandler)
// must consult this so they can't drift apart.
//
// Unauthenticated (expired or invalid session) counts as expected: the
// transport signs the user out with a toast, so it is a handled outcome.
// (Sentry CORTADO-FRONTEND-5)
export function isExpectedError(err: unknown): boolean {
  if (err instanceof ConnectError && err.code === Code.Unauthenticated) return true
  return getErrorMessage(err).expected === true
}

export function mutationErrorHandler(ctx?: ErrorContext): (err: unknown) => void {
  return (err: unknown) => {
    const copy = getErrorMessage(err)
    // Expected, user-actionable errors get a toast but are not reported to Sentry
    // — they are not bugs. Genuine failures (unknown errors, db_create_failed,
    // supabase_create_failed) are still captured.
    if (!copy.expected) {
      reportError(err, { kind: 'mutation', ...ctx })
    }
    toast.error(copy.title, {
      description: copy.description,
      ...(copy.action
        ? {
            action: {
              label: copy.action.label,
              onClick: () => {
                window.location.href = copy.action!.href
              },
            },
          }
        : {}),
    })
  }
}
